import { useState } from "react";
import { Users, Briefcase, TrendingUp, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const positions = [
  {
    title: "District Coordinator",
    description: "Represent The Carbon Meridian in your district. Drive awareness and participation in climate programs.",
    icon: Users,
    requirements: ["Local network", "Communication skills", "Climate interest"]
  },
  {
    title: "Internship",
    description: "Join our team and gain hands-on experience in carbon markets, climate policy, and sustainability.",
    icon: Briefcase,
    requirements: ["Student/Fresh graduate", "Strong academics", "Eager to learn"]
  },
  {
    title: "Carbon Credit Broker",
    description: "Connect carbon credit buyers and sellers. Commission-based role with significant earning potential.",
    icon: TrendingUp,
    requirements: ["Sales experience", "Network in industry", "Understanding of carbon markets"]
  }
];

export default function WorkWithUsPage() {
  const [submitting, setSubmitting] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    experience: "",
    resume_link: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPosition) {
      toast.error("Please select a position");
      return;
    }
    
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/jobs`, {
        ...formData,
        position: selectedPosition
      });
      toast.success("Application submitted successfully! We'll review and get back to you.");
      setFormData({ name: "", email: "", phone: "", experience: "", resume_link: "" });
      setSelectedPosition("");
    } catch (error) {
      console.error("Application error:", error);
      toast.error("Failed to submit application. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="work-with-us-page">
      {/* Hero */}
      <section className="py-16 lg:py-20 bg-gradient-to-br from-forest-50 to-earth-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-montserrat text-4xl sm:text-5xl font-bold text-slate-900 mb-4">
            Work With Us
          </h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            Join India's climate action movement. Be part of building the carbon economy.
          </p>
        </div>
      </section>

      {/* Positions */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-montserrat text-2xl font-bold text-slate-900 mb-8 text-center">
            Open Opportunities
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {positions.map((position) => (
              <Card key={position.title} className="h-full" data-testid={`position-${position.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-forest-50 rounded-xl flex items-center justify-center mb-4">
                    <position.icon className="h-6 w-6 text-forest-700" />
                  </div>
                  <h3 className="font-montserrat text-xl font-bold text-slate-900 mb-2">
                    {position.title}
                  </h3>
                  <p className="text-slate-600 mb-4">{position.description}</p>
                  <p className="text-sm font-medium text-slate-700 mb-2">Requirements:</p>
                  <ul className="space-y-1">
                    {position.requirements.map((req, index) => (
                      <li key={index} className="text-sm text-slate-600 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-forest-500 rounded-full" />
                        {req}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Application Form */}
          <Card className="max-w-2xl mx-auto">
            <CardContent className="p-6 lg:p-8">
              <h3 className="font-montserrat text-xl font-bold text-slate-900 mb-6 text-center">
                Apply Now
              </h3>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="position">Position *</Label>
                  <Select value={selectedPosition} onValueChange={setSelectedPosition}>
                    <SelectTrigger data-testid="select-position">
                      <SelectValue placeholder="Select a position" />
                    </SelectTrigger>
                    <SelectContent>
                      {positions.map((pos) => (
                        <SelectItem key={pos.title} value={pos.title}>
                          {pos.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      data-testid="job-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      data-testid="job-email"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="job-phone"
                  />
                </div>
                <div>
                  <Label htmlFor="experience">Tell us about your experience *</Label>
                  <Textarea
                    id="experience"
                    required
                    rows={4}
                    value={formData.experience}
                    onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    placeholder="Briefly describe your relevant experience and why you want to join us..."
                    data-testid="job-experience"
                  />
                </div>
                <div>
                  <Label htmlFor="resume_link">Resume/LinkedIn Link (optional)</Label>
                  <Input
                    id="resume_link"
                    type="url"
                    value={formData.resume_link}
                    onChange={(e) => setFormData({ ...formData, resume_link: e.target.value })}
                    placeholder="https://linkedin.com/in/your-profile"
                    data-testid="job-resume"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-forest-950 hover:bg-forest-900"
                  disabled={submitting}
                  data-testid="job-submit"
                >
                  {submitting ? "Submitting..." : "Submit Application"}
                  <Send className="ml-2 h-4 w-4" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
